import { WebXRGenericTriggerMotionController } from "./webXRGenericMotionController";
import { Tools } from "../../Misc/tools";
import { WebXRProfiledMotionController } from "./webXRProfiledMotionController";
/**
 * The MotionController Manager manages all registered motion controllers and loads the right one when needed.
 *
 * When this repository is complete: https://github.com/immersive-web/webxr-input-profiles/tree/master/packages/assets
 * it should be replaced with auto-loaded controllers.
 *
 * When using a model try to stay as generic as possible. Eventually there will be no need in any of the controller classes
 */
var WebXRMotionControllerManager = /** @class */ (function () {
    function WebXRMotionControllerManager() {
    }
    /**
     * Clear the cache used for profile loading and reload when requested again
     */
    WebXRMotionControllerManager.ClearProfilesCache = function () {
        this._ProfilesList = null;
        this._ProfileLoadingPromises = {};
    };
    /**
     * Register the default fallbacks.
     * This function is called automatically when this file is imported.
     */
    WebXRMotionControllerManager.DefaultFallbacks = function () {
        this.RegisterFallbacksForProfileId("google-daydream", ["generic-touchpad"]);
        this.RegisterFallbacksForProfileId("htc-vive-focus", ["generic-trigger-touchpad"]);
        this.RegisterFallbacksForProfileId("htc-vive", ["generic-trigger-squeeze-touchpad"]);
        this.RegisterFallbacksForProfileId("magicleap-one", ["generic-trigger-squeeze-touchpad"]);
        this.RegisterFallbacksForProfileId("windows-mixed-reality", ["generic-trigger-squeeze-touchpad-thumbstick"]);
        this.RegisterFallbacksForProfileId("microsoft-mixed-reality", ["windows-mixed-reality", "generic-trigger-squeeze-touchpad-thumbstick"]);
        this.RegisterFallbacksForProfileId("oculus-go", ["generic-trigger-touchpad"]);
        this.RegisterFallbacksForProfileId("oculus-touch-v2", ["oculus-touch", "generic-trigger-squeeze-thumbstick"]);
        this.RegisterFallbacksForProfileId("oculus-touch", ["generic-trigger-squeeze-thumbstick"]);
        this.RegisterFallbacksForProfileId("samsung-gearvr", ["windows-mixed-reality", "generic-trigger-squeeze-touchpad-thumbstick"]);
        this.RegisterFallbacksForProfileId("samsung-odyssey", ["generic-touchpad"]);
        this.RegisterFallbacksForProfileId("valve-index", ["generic-trigger-squeeze-touchpad-thumbstick"]);
    };
    /**
     * Find a fallback profile if the profile was not found. There are a few predefined generic profiles.
     * @param profileId the profile to which a fallback needs to be found
     * @return an array with corresponding fallback profiles
     */
    WebXRMotionControllerManager.FindFallbackWithProfileId = function (profileId) {
        var returnArray = this._Fallbacks[profileId] || [];
        returnArray.unshift(profileId);
        return returnArray;
    };
    /**
     * When acquiring a new xrInput object (usually by the WebXRInput class), match it with the correct profile.
     * The order of search:
     *
     * 1) Iterate the profiles array of the xr input and try finding a corresponding motion controller
     * 2) (If not found) search in the gamepad id and try using it (legacy versions only)
     * 3) search for registered fallbacks (should be redundant, nonetheless it makes sense to check)
     * 4) return the generic trigger controller if none were found
     *
     * @param xrInput the xrInput to which a new controller is initialized
     * @param scene the scene to which the model will be added
     * @param forceProfile force a certain profile for this controller
     * @return A promise that fulfils with the motion controller class for this profile id or the generic standard class if none was found
     */
    WebXRMotionControllerManager.GetMotionControllerWithXRInput = function (xrInput, scene, forceProfile) {
        var _this = this;
        var profileArray = [];
        if (forceProfile) {
            profileArray.push(forceProfile);
        }
        profileArray.push.apply(profileArray, (xrInput.profiles || []));
        // emulator support
        if (profileArray.length && !profileArray[0]) {
            // remove the first "undefined" that the emulator is adding
            profileArray.pop();
        }
        // legacy support - try using the gamepad id
        if (xrInput.gamepad && xrInput.gamepad.id) {
            switch (xrInput.gamepad.id) {
                case xrInput.gamepad.id.match(/oculus touch/gi) ? xrInput.gamepad.id : undefined:
                    // oculus in gamepad id
                    profileArray.push("oculus-touch-v2");
                    break;
            }
        }
        // make sure microsoft/windows mixed reality works correctly
        var windowsMRIdx = profileArray.indexOf("windows-mixed-reality");
        if (windowsMRIdx !== -1) {
            profileArray.splice(windowsMRIdx, 0, "microsoft-mixed-reality");
        }
        if (!profileArray.length) {
            profileArray.push("generic-trigger");
        }
        if (this.UseOnlineRepository) {
            var firstFunction = this.PrioritizeOnlineRepository ? this._LoadProfileFromRepository : this._LoadProfilesFromAvailableControllers;
            var secondFunction_1 = this.PrioritizeOnlineRepository ? this._LoadProfilesFromAvailableControllers : this._LoadProfileFromRepository;
            return firstFunction.call(this, profileArray, xrInput, scene).catch(function () {
                return secondFunction_1.call(_this, profileArray, xrInput, scene);
            });
        }
        else {
            // use only available functions
            return this._LoadProfilesFromAvailableControllers(profileArray, xrInput, scene);
        }
    };
    /**
     * Register a new controller based on its profile. This function will be called by the controller classes themselves.
     *
     * If you are missing a profile, make sure it is imported in your source, otherwise it will not register.
     *
     * @param type the profile type to register
     * @param constructFunction the function to be called when loading this profile
     */
    WebXRMotionControllerManager.RegisterController = function (type, constructFunction) {
        this._AvailableControllers[type] = constructFunction;
    };
    /**
     * Register a fallback to a specific profile.
     * @param profileId the profileId that will receive the fallbacks
     * @param fallbacks A list of fallback profiles
     */
    WebXRMotionControllerManager.RegisterFallbacksForProfileId = function (profileId, fallbacks) {
        var _a;
        if (this._Fallbacks[profileId]) {
            (_a = this._Fallbacks[profileId]).push.apply(_a, fallbacks);
        }
        else {
            this._Fallbacks[profileId] = fallbacks;
        }
    };
    /**
     * Will update the list of profiles available in the repository
     * @return a promise that resolves to a map of profiles available online
     */
    WebXRMotionControllerManager.UpdateProfilesList = function () {
        this._ProfilesList = Tools.LoadFileAsync(this.BaseRepositoryUrl + "/profiles/profilesList.json", false).then(function (data) {
            return JSON.parse(data.toString());
        });
        return this._ProfilesList;
    };
    WebXRMotionControllerManager._LoadProfileFromRepository = function (profileArray, xrInput, scene) {
        var _this = this;
        return Promise.resolve()
            .then(function () {
            if (!_this._ProfilesList) {
                return _this.UpdateProfilesList();
            }
            else {
                return _this._ProfilesList;
            }
        })
            .then(function (profilesList) {
            // load the right profile
            for (var i = 0; i < profileArray.length; ++i) {
                // defensive
                if (!profileArray[i]) {
                    continue;
                }
                if (profilesList[profileArray[i]]) {
                    return profileArray[i];
                }
            }
            throw new Error("neither controller " + profileArray[0] + " nor all fallbacks were found in the repository,");
        })
            .then(function (profileToLoad) {
            // load the profile
            if (!_this._ProfileLoadingPromises[profileToLoad]) {
                _this._ProfileLoadingPromises[profileToLoad] = Tools.LoadFileAsync(_this.BaseRepositoryUrl + "/profiles/" + profileToLoad + "/profile.json", false).then(function (data) { return JSON.parse(data); });
            }
            return _this._ProfileLoadingPromises[profileToLoad];
        })
            .then(function (profile) {
            return new WebXRProfiledMotionController(scene, xrInput, profile, _this.BaseRepositoryUrl);
        });
    };
    WebXRMotionControllerManager._LoadProfilesFromAvailableControllers = function (profileArray, xrInput, scene) {
        // check fallbacks
        for (var i = 0; i < profileArray.length; ++i) {
            // defensive
            if (!profileArray[i]) {
                continue;
            }
            var fallbacks = this.FindFallbackWithProfileId(profileArray[i]);
            for (var j = 0; j < fallbacks.length; ++j) {
                var constructionFunction = this._AvailableControllers[fallbacks[j]];
                if (constructionFunction) {
                    return Promise.resolve(constructionFunction(xrInput, scene));
                }
            }
        }
        throw new Error("no controller requested was found in the available controllers list");
    };
    WebXRMotionControllerManager._AvailableControllers = {};
    WebXRMotionControllerManager._Fallbacks = {};
    // cache for loading
    WebXRMotionControllerManager._ProfileLoadingPromises = {};
    /**
     * The base URL of the online controller repository. Can be changed at any time.
     */
    WebXRMotionControllerManager.BaseRepositoryUrl = "https://immersive-web.github.io/webxr-input-profiles/packages/viewer/dist";
    /**
     * Which repository gets priority - local or online
     */
    WebXRMotionControllerManager.PrioritizeOnlineRepository = true;
    /**
     * Use the online repository, or use only locally-defined controllers
     */
    WebXRMotionControllerManager.UseOnlineRepository = true;
    return WebXRMotionControllerManager;
}());
export { WebXRMotionControllerManager };
// register the generic profile(s) here so we will at least have them
WebXRMotionControllerManager.RegisterController(WebXRGenericTriggerMotionController.ProfileId, function (xrInput, scene) {
    return new WebXRGenericTriggerMotionController(scene, xrInput.gamepad, xrInput.handedness);
});
// register fallbacks
WebXRMotionControllerManager.DefaultFallbacks();
//# sourceMappingURL=webXRMotionControllerManager.js.map